import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Typography, Grid, Paper, Card, CardContent, Stack, Avatar, Button,
  Divider, Tabs, Tab, Chip, LinearProgress, IconButton, Skeleton, alpha,
} from '@mui/material';
import {
  People, Business, Bookmark, Payment, SupportAgent, VerifiedUser,
  TrendingUp, ArrowForward, Warning, Description, Group, SmartToy,
  Sms, Settings, Gavel, ConfirmationNumber, ArrowUpward, ArrowDownward,
  CalendarMonth, AttachMoney, Home, PersonAdd, CheckCircle, Cancel,
  Speed, Timer, Star, Refresh,
} from '@mui/icons-material';
import {
  LineChart as RechartsLineChart, Line,
  BarChart as RechartsBarChart, Bar,
  PieChart as RechartsPieChart, Pie,
  AreaChart, Area, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { adminAPI } from '../../api/client';

const GRADIENT_CARDS = [
  { bg: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', shadow: 'rgba(99,102,241,0.4)' },
  { bg: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)', shadow: 'rgba(16,185,129,0.4)' },
  { bg: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)', shadow: 'rgba(59,130,246,0.4)' },
  { bg: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)', shadow: 'rgba(245,158,11,0.4)' },
  { bg: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)', shadow: 'rgba(239,68,68,0.4)' },
  { bg: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)', shadow: 'rgba(139,92,246,0.4)' },
];

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6'];

const quickLinks = [
  { text: 'Users', icon: <People />, path: '/admin/users', color: '#6366f1' },
  { text: 'KYC', icon: <VerifiedUser />, path: '/admin/kyc', color: '#10b981' },
  { text: 'Properties', icon: <Business />, path: '/admin/properties', color: '#3b82f6' },
  { text: 'Bookings', icon: <Bookmark />, path: '/admin/bookings', color: '#f59e0b' },
  { text: 'Tickets', icon: <SupportAgent />, path: '/admin/tickets', color: '#ef4444' },
  { text: 'Payments', icon: <Payment />, path: '/admin/payments', color: '#8b5cf6' },
  { text: 'Tenancy', icon: <Description />, path: '/admin/tenancy', color: '#14b8a6' },
  { text: 'Room Pairing', icon: <Group />, path: '/admin/room-pairing', color: '#ec4899' },
  { text: 'AI Settings', icon: <SmartToy />, path: '/admin/settings/ai', color: '#6366f1' },
  { text: 'SMS', icon: <Sms />, path: '/admin/settings/sms', color: '#f59e0b' },
  { text: 'System', icon: <Settings />, path: '/admin/settings/system', color: '#64748b' },
];

function GradientStatCard({ icon, title, value, subtitle, trend, trendUp, gradient, shadowColor, link }) {
  return (
    <Card sx={{
      height: '100%', background: gradient, color: 'white', position: 'relative', overflow: 'hidden',
      boxShadow: `0 10px 30px -5px ${shadowColor}`, border: 'none',
      '&::before': {
        content: '""', position: 'absolute', top: -20, right: -20, width: 120, height: 120,
        borderRadius: '50%', background: 'rgba(255,255,255,0.1)',
      },
      '&::after': {
        content: '""', position: 'absolute', bottom: -30, right: 40, width: 80, height: 80,
        borderRadius: '50%', background: 'rgba(255,255,255,0.08)',
      },
    }}>
      <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="body2" sx={{ opacity: 0.85, mb: 0.5, fontWeight: 500 }}>{title}</Typography>
            <Typography variant="h3" fontWeight={800} sx={{ lineHeight: 1.1, mb: 0.5 }}>{value}</Typography>
            {subtitle && <Typography variant="caption" sx={{ opacity: 0.75 }}>{subtitle}</Typography>}
            {trend !== undefined && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
                {trendUp ? <ArrowUpward sx={{ fontSize: 16 }} /> : <ArrowDownward sx={{ fontSize: 16 }} />}
                <Typography variant="caption" fontWeight={700}>{trend}% from last month</Typography>
              </Stack>
            )}
          </Box>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56, backdropFilter: 'blur(10px)' }}>
            {icon}
          </Avatar>
        </Stack>
        {link && (
          <Button component={Link} to={link} size="small" endIcon={<ArrowForward />}
            sx={{ mt: 1.5, color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)', boxShadow: 'none' } }}>
            Manage
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <Paper sx={{ p: 1.5, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        {payload.map((p, i) => (
          <Typography key={i} variant="body2" fontWeight={600} sx={{ color: p.color }}>
            {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString() : p.value}
          </Typography>
        ))}
      </Paper>
    );
  }
  return null;
}

function AdminDashboard() {
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, analyticsRes] = await Promise.all([
        adminAPI.getDashboard(),
        adminAPI.getAnalytics(30)
      ]);
      setData(dashboardRes.data);
      setAnalytics(analyticsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const usersByRole = data ? [
    { name: 'Students', value: data.users.tenants || 0 },
    { name: 'Landlords', value: data.users.landlords || 0 },
    { name: 'Agents', value: data.users.agents || 0 },
    { name: 'Staff', value: (data.users.total || 0) - (data.users.tenants || 0) - (data.users.landlords || 0) - (data.users.agents || 0) },
  ].filter(d => d.value > 0) : [];

  const propertyTypes = data?.properties ? [
    { name: 'Active', value: data.properties.active || 0 },
    { name: 'Pending', value: data.properties.pending_review || 0 },
    { name: 'Inactive', value: Math.max(0, (data.properties.total || 0) - (data.properties.active || 0) - (data.properties.pending_review || 0)) },
  ].filter(d => d.value > 0) : [];

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={300} height={50} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={200} height={24} sx={{ mb: 3 }} />
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[1,2,3,4].map(i => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={160} sx={{ borderRadius: 4 }} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={3}>
          {[1,2].map(i => (
            <Grid item xs={12} md={6} key={i}>
              <Skeleton variant="rounded" height={350} sx={{ borderRadius: 4 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">Failed to load dashboard</Typography>
        <Button onClick={fetchData} startIcon={<Refresh />} sx={{ mt: 2 }}>Retry</Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Super Admin Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Complete platform overview and management control center
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Chip icon={<CalendarMonth />} label={new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} variant="outlined" />
          <IconButton onClick={fetchData} sx={{ bgcolor: 'white', boxShadow: 1 }}><Refresh /></IconButton>
        </Stack>
      </Box>

      {/* Tabs */}
      <Paper sx={{ borderRadius: 3, mb: 3, px: 1 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}
          sx={{ '& .MuiTabs-indicator': { height: 3, borderRadius: 2 } }}>
          <Tab label="Overview" icon={<Speed sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Analytics & Reports" icon={<TrendingUp sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Quick Actions" icon={<Settings sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab 0: Overview */}
      {tabValue === 0 && (
        <>
          {/* Main KPI Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <GradientStatCard icon={<People />} title="Total Users" value={data.users.total || 0}
                subtitle={`${data.users.tenants || 0} students, ${data.users.landlords || 0} landlords`}
                trend={12} trendUp gradient={GRADIENT_CARDS[0].bg} shadowColor={GRADIENT_CARDS[0].shadow} link="/admin/users" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <GradientStatCard icon={<Business />} title="Properties" value={data.properties.total || 0}
                subtitle={`${data.properties.active || 0} active, ${data.properties.pending_review || 0} pending`}
                trend={8} trendUp gradient={GRADIENT_CARDS[1].bg} shadowColor={GRADIENT_CARDS[1].shadow} link="/admin/properties" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <GradientStatCard icon={<Bookmark />} title="Bookings" value={data.bookings.total || 0}
                subtitle={`${data.bookings.active || 0} active bookings`}
                trend={15} trendUp gradient={GRADIENT_CARDS[2].bg} shadowColor={GRADIENT_CARDS[2].shadow} link="/admin/bookings" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <GradientStatCard icon={<AttachMoney />} title="Revenue"
                value={`GH\u20B5${(data.revenue?.total || 0).toLocaleString()}`}
                subtitle="Total collected" trend={22} trendUp
                gradient={GRADIENT_CARDS[3].bg} shadowColor={GRADIENT_CARDS[3].shadow} link="/admin/payments" />
            </Grid>
          </Grid>

          {/* Secondary KPI Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', borderLeft: '4px solid #ef4444' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" color="text.secondary">Open Tickets</Typography>
                      <Typography variant="h4" fontWeight={800} color="error.main">{data.tickets?.open || 0}</Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: alpha('#ef4444', 0.1), color: 'error.main' }}><SupportAgent /></Avatar>
                  </Stack>
                  <Button component={Link} to="/admin/tickets" size="small" endIcon={<ArrowForward />} sx={{ mt: 1 }}>View All</Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', borderLeft: '4px solid #f59e0b' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" color="text.secondary">Pending KYC</Typography>
                      <Typography variant="h4" fontWeight={800} color="warning.main">{data.kyc?.pending || 0}</Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: alpha('#f59e0b', 0.1), color: 'warning.main' }}><VerifiedUser /></Avatar>
                  </Stack>
                  <Button component={Link} to="/admin/kyc" size="small" endIcon={<ArrowForward />} sx={{ mt: 1 }}>Review</Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', borderLeft: '4px solid #10b981' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" color="text.secondary">Active Tenancies</Typography>
                      <Typography variant="h4" fontWeight={800} color="success.main">{data.tenancy?.active || 0}</Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: alpha('#10b981', 0.1), color: 'success.main' }}><Gavel /></Avatar>
                  </Stack>
                  <Button component={Link} to="/admin/tenancy" size="small" endIcon={<ArrowForward />} sx={{ mt: 1 }}>Manage</Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', borderLeft: '4px solid #6366f1' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" color="text.secondary">Pending Properties</Typography>
                      <Typography variant="h4" fontWeight={800} color="primary.main">{data.properties?.pending_review || 0}</Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: alpha('#6366f1', 0.1), color: 'primary.main' }}><Home /></Avatar>
                  </Stack>
                  <Button component={Link} to="/admin/properties" size="small" endIcon={<ArrowForward />} sx={{ mt: 1 }}>Review</Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            {/* Action Items */}
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>Action Items</Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  {data.properties.pending_review > 0 && (
                    <Paper variant="outlined" sx={{ p: 2, borderColor: 'warning.main', borderRadius: 2, borderLeftWidth: 4, bgcolor: alpha('#f59e0b', 0.03) }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Avatar sx={{ bgcolor: alpha('#f59e0b', 0.1), color: 'warning.main', width: 40, height: 40 }}><Warning /></Avatar>
                          <Box>
                            <Typography fontWeight={600}>{data.properties.pending_review} Properties Pending</Typography>
                            <Typography variant="caption" color="text.secondary">Awaiting review & approval</Typography>
                          </Box>
                        </Stack>
                        <Button component={Link} to="/admin/properties" size="small" variant="outlined" color="warning">Review</Button>
                      </Stack>
                    </Paper>
                  )}
                  {data.kyc.pending > 0 && (
                    <Paper variant="outlined" sx={{ p: 2, borderColor: 'info.main', borderRadius: 2, borderLeftWidth: 4, bgcolor: alpha('#3b82f6', 0.03) }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Avatar sx={{ bgcolor: alpha('#3b82f6', 0.1), color: 'info.main', width: 40, height: 40 }}><VerifiedUser /></Avatar>
                          <Box>
                            <Typography fontWeight={600}>{data.kyc.pending} KYC Verifications</Typography>
                            <Typography variant="caption" color="text.secondary">Identity documents to verify</Typography>
                          </Box>
                        </Stack>
                        <Button component={Link} to="/admin/kyc" size="small" variant="outlined" color="info">Verify</Button>
                      </Stack>
                    </Paper>
                  )}
                  {data.tickets.open > 0 && (
                    <Paper variant="outlined" sx={{ p: 2, borderColor: 'error.main', borderRadius: 2, borderLeftWidth: 4, bgcolor: alpha('#ef4444', 0.03) }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Avatar sx={{ bgcolor: alpha('#ef4444', 0.1), color: 'error.main', width: 40, height: 40 }}><SupportAgent /></Avatar>
                          <Box>
                            <Typography fontWeight={600}>{data.tickets.open} Open Tickets</Typography>
                            <Typography variant="caption" color="text.secondary">Support tickets needing attention</Typography>
                          </Box>
                        </Stack>
                        <Button component={Link} to="/admin/tickets" size="small" variant="outlined" color="error">View</Button>
                      </Stack>
                    </Paper>
                  )}
                  {data.properties.pending_review === 0 && data.kyc.pending === 0 && data.tickets.open === 0 && (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <CheckCircle color="success" sx={{ fontSize: 48, mb: 1 }} />
                      <Typography fontWeight={600}>All caught up!</Typography>
                      <Typography variant="body2" color="text.secondary">No pending actions at this time.</Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Grid>

            {/* Users by Role Pie Chart */}
            <Grid item xs={12} md={3.5}>
              <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>Users by Role</Typography>
                <Divider sx={{ mb: 2 }} />
                <ResponsiveContainer width="100%" height={220}>
                  <RechartsPieChart>
                    <Pie data={usersByRole} cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                      fill="#8884d8" dataKey="value" paddingAngle={4} cornerRadius={6}>
                      {usersByRole.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </RechartsPieChart>
                </ResponsiveContainer>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {usersByRole.map((item, i) => (
                    <Stack key={i} direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: COLORS[i % COLORS.length] }} />
                        <Typography variant="body2">{item.name}</Typography>
                      </Stack>
                      <Typography variant="body2" fontWeight={700}>{item.value}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>

            {/* Property Types Pie Chart */}
            <Grid item xs={12} md={3.5}>
              <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>Property Status</Typography>
                <Divider sx={{ mb: 2 }} />
                <ResponsiveContainer width="100%" height={220}>
                  <RechartsPieChart>
                    <Pie data={propertyTypes} cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                      fill="#8884d8" dataKey="value" paddingAngle={4} cornerRadius={6}>
                      {propertyTypes.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[(index + 1) % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </RechartsPieChart>
                </ResponsiveContainer>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {propertyTypes.map((item, i) => (
                    <Stack key={i} direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: COLORS[(i + 1) % COLORS.length] }} />
                        <Typography variant="body2">{item.name}</Typography>
                      </Stack>
                      <Typography variant="body2" fontWeight={700}>{item.value}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {/* Tab 1: Analytics & Reports */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          {/* User Growth */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Platform Growth</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>User registrations over the last 30 days</Typography>
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={analytics?.user_registrations || []}>
                  <defs>
                    <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="count" name="Registrations" stroke="#6366f1" strokeWidth={3} fill="url(#colorUsers)" />
                </AreaChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Booking Trends */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Booking Status</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Distribution by status</Typography>
              <ResponsiveContainer width="100%" height={250}>
                <RechartsPieChart>
                  <Pie data={analytics?.booking_breakdown || []} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                    fill="#8884d8" dataKey="count" nameKey="status" paddingAngle={4} cornerRadius={6}>
                    {(analytics?.booking_breakdown || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </RechartsPieChart>
              </ResponsiveContainer>
              <Stack spacing={0.5}>
                {(analytics?.booking_breakdown || []).map((item, i) => (
                  <Stack key={i} direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: COLORS[i % COLORS.length] }} />
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{item.status}</Typography>
                    </Stack>
                    <Typography variant="body2" fontWeight={700}>{item.count}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Grid>

          {/* Revenue Trend */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Revenue Analytics</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Revenue trend over time</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={analytics?.revenue_trend || []}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="amount" name="Revenue (GH₵)" stroke="#10b981" strokeWidth={3} fill="url(#colorRevenue)" />
                </AreaChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Property Submissions */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Property Submissions</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>New listings submitted over time</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsBarChart data={analytics?.property_submissions || []} barSize={20}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Submissions" fill="#6366f1" radius={[6, 6, 0, 0]} />
                </RechartsBarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Platform Performance */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Platform Performance Summary</Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={3}>
                <Grid item xs={6} sm={4} md={2}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Avatar sx={{ bgcolor: alpha('#6366f1', 0.1), color: '#6366f1', width: 56, height: 56, mx: 'auto', mb: 1 }}>
                      <People />
                    </Avatar>
                    <Typography variant="h5" fontWeight={800}>{data.users.total || 0}</Typography>
                    <Typography variant="caption" color="text.secondary">Total Users</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Avatar sx={{ bgcolor: alpha('#10b981', 0.1), color: '#10b981', width: 56, height: 56, mx: 'auto', mb: 1 }}>
                      <Business />
                    </Avatar>
                    <Typography variant="h5" fontWeight={800}>{data.properties.total || 0}</Typography>
                    <Typography variant="caption" color="text.secondary">Properties</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Avatar sx={{ bgcolor: alpha('#3b82f6', 0.1), color: '#3b82f6', width: 56, height: 56, mx: 'auto', mb: 1 }}>
                      <Bookmark />
                    </Avatar>
                    <Typography variant="h5" fontWeight={800}>{data.bookings.total || 0}</Typography>
                    <Typography variant="caption" color="text.secondary">Bookings</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Avatar sx={{ bgcolor: alpha('#f59e0b', 0.1), color: '#f59e0b', width: 56, height: 56, mx: 'auto', mb: 1 }}>
                      <ConfirmationNumber />
                    </Avatar>
                    <Typography variant="h5" fontWeight={800}>{(data.tickets?.open || 0) + (data.tickets?.resolved || 0)}</Typography>
                    <Typography variant="caption" color="text.secondary">Tickets</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Avatar sx={{ bgcolor: alpha('#ef4444', 0.1), color: '#ef4444', width: 56, height: 56, mx: 'auto', mb: 1 }}>
                      <VerifiedUser />
                    </Avatar>
                    <Typography variant="h5" fontWeight={800}>{data.kyc?.pending || 0}</Typography>
                    <Typography variant="caption" color="text.secondary">KYC Pending</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Avatar sx={{ bgcolor: alpha('#8b5cf6', 0.1), color: '#8b5cf6', width: 56, height: 56, mx: 'auto', mb: 1 }}>
                      <AttachMoney />
                    </Avatar>
                    <Typography variant="h5" fontWeight={800}>GH₵{((data.revenue?.total || 0) / 1000).toFixed(1)}k</Typography>
                    <Typography variant="caption" color="text.secondary">Revenue</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Tab 2: Quick Actions */}
      {tabValue === 2 && (
        <Box>
          <Typography variant="h6" fontWeight={700} gutterBottom>Management Modules</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Quick access to all platform management sections
          </Typography>
          <Grid container spacing={2}>
            {quickLinks.map((link, i) => (
              <Grid item xs={6} sm={4} md={3} key={i}>
                <Card component={Link} to={link.path} sx={{
                  textDecoration: 'none', cursor: 'pointer', height: '100%',
                  transition: 'all 0.3s ease',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 12px 24px ${alpha(link.color, 0.2)}` },
                }}>
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Avatar sx={{ bgcolor: alpha(link.color, 0.1), color: link.color, width: 64, height: 64, mx: 'auto', mb: 2 }}>
                      {link.icon}
                    </Avatar>
                    <Typography variant="subtitle1" fontWeight={700} color="text.primary">{link.text}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
}

export default AdminDashboard;
